import moment from "moment";
import {
  defaultLeaderboardComparePeriod,
  defaultLeaderboardTimePeriod,
  leaderboardComparePeriods,
  leaderboardTimePeriods,
} from "features/Leaderboards/LeaderboardTables/LeaderboardEditable/LeaderboardEditable.config";
import {
  LeaderboardComparePeriod,
  LeaderboardFormValues,
  LeaderboardTimePeriod,
} from "features/Leaderboards/LeaderboardTables/LeaderboardEditable/LeaderboardEditable.types";
import { LeaderboardBase, LeaderboardRankType } from "types/leaderboard";
import { Exercise, FireStoreMeasurement, ScoreMetadata } from "types";
import { Circuit } from "types/circuit";
import CircuitHelpers from "helpers/CircuitHelpers";
import { Group } from "types/groups";
import { DocumentData, QuerySnapshot } from "firebase/firestore";
import MeasurementService from "services/MeasurementService";

class LeaderboardHelpers {
  static toForm(
    leaderboard: LeaderboardBase,
    options: { groups: Group[]; exercises: Exercise[]; scores: ScoreMetadata[]; circuits: Circuit[] }
  ): LeaderboardFormValues {
    const {
      name,
      groups,
      measureType,
      exerciseId,
      metricId,
      type,
      startDateComparePeriod,
      comparePeriodType,
      rankType,
      groupIds,
      ...restOfLeaderboard
    } = leaderboard;

    // Handle Score type leaderboards
    if (measureType === "Score") {
      // Find score metadata by matching metricId to scoreMetadata.field
      const scoreMetadata = (metricId && options.scores.find(({ field }) => field === metricId)) || null;

      return {
        ...restOfLeaderboard,
        measureType: "Score",
        name,
        scoreMetadata,
        type: LeaderboardHelpers.getTimePeriod(type),
        startDateComparePeriod: comparePeriodType === "Custom" ? startDateComparePeriod : null,
        comparePeriodType: LeaderboardHelpers.getComparePeriod(comparePeriodType),
        rankType: rankType || LeaderboardRankType.Absolute,
        groups: options.groups.filter((group) => groupIds.includes(group.id)),
      };
    }

    // Handle Circuit type leaderboards (exerciseId carries the circuit id)
    if (measureType === "Circuit") {
      const circuit = (exerciseId && options.circuits.find(({ id }) => id === exerciseId)) || null;
      const circuitMetric =
        (circuit && CircuitHelpers.getPbEnabledMetrics(circuit).find(({ field }) => field === metricId)) || null;

      return {
        ...restOfLeaderboard,
        measureType: "Circuit",
        name,
        circuit,
        metric: circuitMetric,
        type: LeaderboardHelpers.getTimePeriod(type),
        startDateComparePeriod: comparePeriodType === "Custom" ? startDateComparePeriod : null,
        comparePeriodType: LeaderboardHelpers.getComparePeriod(comparePeriodType),
        rankType: rankType || LeaderboardRankType.Absolute,
        groups: options.groups.filter((group) => groupIds.includes(group.id)),
      };
    }

    // Handle Exercise type leaderboards
    const exercise = (exerciseId && options.exercises.find(({ id }) => id === exerciseId)) || null;
    const metric = exercise?.metrics.find(({ field, sortType }) => field === metricId && sortType !== null) || null;

    return {
      ...restOfLeaderboard,
      measureType: "Exercise",
      name,
      exercise,
      metric,
      variant: leaderboard.variant || exercise?.variants[0] || null,
      type: LeaderboardHelpers.getTimePeriod(type),
      startDateComparePeriod: comparePeriodType === "Custom" ? startDateComparePeriod : null,
      comparePeriodType: LeaderboardHelpers.getComparePeriod(comparePeriodType),
      rankType: rankType || LeaderboardRankType.Absolute,
      groups: options.groups.filter((group) => groupIds.includes(group.id)),
    };
  }

  static fromForm(leaderboard: LeaderboardFormValues): LeaderboardBase {
    const { groups, type, startDateComparePeriod, rankType: formRankType, comparePeriodType } = leaderboard;

    const rankType = formRankType || LeaderboardRankType.Absolute;

    // Handle Score type leaderboards
    if (leaderboard.measureType === "Score") {
      const { scoreMetadata } = leaderboard;

      if (!scoreMetadata) {
        throw new Error("Score metadata not set");
      }

      return {
        name: leaderboard.name || scoreMetadata.name,
        measureType: "Score",
        exerciseId: null,
        metricId: scoreMetadata.field,
        variant: null,
        /**
         * @deprecated In favour of groupIds
         * TODO: Remove */
        groups: groups.map(({ id, name, colour }) => ({ id, name, colour })),
        groupIds: groups.map(({ id }) => id),
        type: type.value,
        startDateComparePeriod:
          rankType === LeaderboardRankType.PercentageOfBest ? moment(startDateComparePeriod).toISOString() : null,
        rankType,
        comparePeriodType: rankType === LeaderboardRankType.PercentageOfBest ? comparePeriodType.value : null,
        endTime: null,
        startTime: null,
      };
    }

    // Handle Circuit type leaderboards (exerciseId carries the circuit id, no variant)
    if (leaderboard.measureType === "Circuit") {
      const { circuit, metric } = leaderboard;

      if (!(circuit && metric)) {
        throw new Error("Circuit and Metric not set");
      }

      return {
        name: leaderboard.name || LeaderboardHelpers.getDefaultName(leaderboard),
        measureType: "Circuit",
        exerciseId: circuit.id,
        metricId: metric.field,
        variant: null,
        /**
         * @deprecated In favour of groupIds
         * TODO: Remove */
        groups: groups.map(({ id, name, colour }) => ({ id, name, colour })),
        groupIds: groups.map(({ id }) => id),
        type: type.value,
        startDateComparePeriod:
          rankType === LeaderboardRankType.PercentageOfBest ? moment(startDateComparePeriod).toISOString() : null,
        rankType,
        comparePeriodType: rankType === LeaderboardRankType.PercentageOfBest ? comparePeriodType.value : null,
        endTime: null,
        startTime: null,
      };
    }

    // Handle Exercise type leaderboards
    const { exercise, metric, variant } = leaderboard;

    if (!(exercise && metric)) {
      throw new Error("Exercise and Metric not set");
    }

    return {
      name: leaderboard.name || LeaderboardHelpers.getDefaultName(leaderboard),
      measureType: "Exercise",
      exerciseId: exercise.id,
      metricId: metric.field,
      variant,
      /**
       * @deprecated In favour of groupIds
       * TODO: Remove */
      groups: groups.map(({ id, name, colour }) => ({ id, name, colour })),
      groupIds: groups.map(({ id }) => id),
      type: type.value,
      startDateComparePeriod:
        rankType === LeaderboardRankType.PercentageOfBest ? moment(startDateComparePeriod).toISOString() : null,
      rankType,
      comparePeriodType: rankType === LeaderboardRankType.PercentageOfBest ? comparePeriodType.value : null,
      endTime: null,
      startTime: null,
    };
  }

  static getTimePeriod(type: LeaderboardTimePeriod["value"] | "Custom") {
    return leaderboardTimePeriods.find((period) => period.value === type) || defaultLeaderboardTimePeriod;
  }

  static getComparePeriod(comparePeriodType: LeaderboardComparePeriod["value"] | null) {
    return (
      leaderboardComparePeriods.find((period) => period.value === comparePeriodType) || defaultLeaderboardComparePeriod
    );
  }

  static getDefaultName(leaderboard: LeaderboardFormValues) {
    if (leaderboard.measureType === "Score") {
      return leaderboard.scoreMetadata?.name || "Score Leaderboard";
    }

    if (leaderboard.measureType === "Circuit") {
      return [leaderboard.circuit?.name, leaderboard.metric?.name].filter(Boolean).join(" | ");
    }

    return [leaderboard.exercise?.name, leaderboard.variant?.charAt(0), leaderboard.metric?.name]
      .filter(Boolean)
      .join(" | ");
  }

  static onSnapshot(
    snapshot: QuerySnapshot<DocumentData>,
    updateStore: (newAndUpdateMeasurements: { [id: string]: FireStoreMeasurement | undefined }) => void,
    { metricField }: { metricField: string }
  ) {
    const measurementUpdates: { [id: string]: FireStoreMeasurement | undefined } = {};

    snapshot.docChanges().forEach((docChange) => {
      if (docChange.type === "removed" || !docChange.doc.data()[metricField]) {
        measurementUpdates[docChange.doc.id] = undefined;
        return;
      }

      const docData = docChange.doc.data();

      // Check if this is a Hyrox/Score document (has createdAt instead of completedDate)
      const isScoreDocument = docData.createdAt && !docData.completedDate;

      const document = isScoreDocument
        ? {
            id: docChange.doc.id,
            ...docData,
            // Convert createdAt Timestamp to ISO string for consistency with exercise measurements
            completedDate: moment(new Date(docData.createdAt.toDate())).toISOString(),
          }
        : MeasurementService.deserializeFirestoreDoc(docChange.doc);

      measurementUpdates[docChange.doc.id] = document;
    });

    updateStore(measurementUpdates);
  }
}
export default LeaderboardHelpers;
